import React, { useState, useCallback } from 'react';
import { Mail, Phone, MapPin, User, MessageSquare, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { IconBrandFacebook, IconBrandInstagram, IconBrandGithub, IconBrandLinkedin } from '@tabler/icons-react';
import { Sec, FormField, TextareaField } from '../components/Shared';
import { CONTACT_SCRIPT_URL } from '../data/websiteData';

const ContactPage = ({ showToast, nl, setNl, nlSt, submitNL }) => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const handleChange = useCallback((id, val) => {
    setForm(p => ({ ...p, [id]: val }));
    if (errors[id]) setErrors(p => ({ ...p, [id]: '' }));
  }, [errors]);

  const submit = async e => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.subject.trim()) errs.subject = 'Required';
    if (!form.message.trim()) errs.message = 'Required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('loading');
    try {
      const res = await fetch(CONTACT_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          type: 'Contact',
          timestamp: new Date().toISOString(),
          Timestamp: new Date().toISOString(),
          Name: form.name,
          Email: form.email,
          Subject: form.subject,
          Message: form.message
        })
      });

      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.status === 'error') throw new Error(data.message || 'API Error');

      setStatus('success');
      showToast('Transmission received. We will respond shortly.', 'success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('idle');
      showToast('Submission failed. Check your uplink.', 'error');
    }
  };

  return (
    <div className="af">
      <Sec py={160} bg="var(--grad-hero)">
        <div className="g5c">
          <div className="au">
            <div className="eyebrow">Uplink Protocols</div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,64px)', fontWeight: 800, color: 'var(--on-surface)', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: 32 }}>Open the <span className="gtext">Channel</span></h1>
            <p style={{ fontSize: 18, color: 'var(--on-surface-variant)', lineHeight: 1.7, marginBottom: 48, fontWeight: 500 }}>Questions regarding enrollment, collaborations or technical events? Our operational team is ready to assist.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              {[
                { ico: <Mail size={20} />, l: 'Email Us', v: 'info@icditc.club', d: 'Replies within 48 hours' },
                { ico: <MapPin size={20} />, l: '65 Central Rd', v: 'Ideal College, Dhanmondi', d: 'Dhaka-1205, Bangladesh' },
                { ico: <Phone size={20} />, l: 'Emergency Contect', v: '880 1301434437', d: '10am-8pm' }
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 20 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--surface-container-low)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.ico}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 2 }}>{item.v}</div>
                    <div style={{ fontSize: 11, color: 'var(--on-surface-variant)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{item.l} • {item.d}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 64, display: 'flex', gap: 16 }}>
              {[{ icon: <IconBrandFacebook size={20} />, c: '#3B82F6', link: 'https://www.facebook.com/icditc' }, { icon: <IconBrandInstagram size={20} />, c: '#EC4899', link: 'https://www.instagram.com/icditc' }, { icon: <IconBrandLinkedin size={20} />, c: '#0077B5', link: 'https://linkedin.com/in/icditc' }, { icon: <IconBrandGithub size={20} />, c: '#191C1E', link: 'https://github.com/icditc' }].map((item, i) => (
                <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--surface-container-low)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-surface-variant)', textDecoration: 'none', transition: 'all .3s' }}
                  onMouseOver={e => e.currentTarget.style.color = item.c}
                  onMouseOut={e => e.currentTarget.style.color = 'var(--on-surface-variant)'}
                >{item.icon}</a>
              ))}
            </div>
          </div>

          <div className="au d2">
            <div className="card form-card" style={{ background: 'var(--surface)' }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                    <CheckCircle2 size={32} style={{ color: 'var(--green)' }} />
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 12 }}>Signal Locked</h3>
                  <p style={{ color: 'var(--on-surface-variant)', marginBottom: 32 }}>We've received your transmission. A member of our technical team will establish a secure uplink shortly.</p>
                  <button className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setStatus('idle')}>New Transmission</button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div className="g2">
                    <FormField id="name" label="Full Name" placeholder="Jane Doe" value={form.name} onChange={handleChange} error={errors.name} icon={<User size={14} />} />
                    <FormField id="email" label="Email Address" placeholder="jane@example.com" type="email" value={form.email} onChange={handleChange} error={errors.email} icon={<Mail size={14} />} />
                  </div>
                  <FormField id="subject" label="Subject" placeholder="General Inquiry" value={form.subject} onChange={handleChange} error={errors.subject} icon={<MessageSquare size={14} />} />
                  <TextareaField id="message" label="Your Message" placeholder="Describe your request..." value={form.message} onChange={handleChange} error={errors.message} />
                  <button className="btn btn-primary btn-lg" type="submit" disabled={status === 'loading'} style={{ width: '100%', justifyContent: 'center' }}>
                    {status === 'loading' ? <><Loader2 size={18} className="spin" /> Sending…</> : <><Send size={18} /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </Sec>
    </div>
  );
};

export default ContactPage;
